import {
  Home,
  Users,
  Calendar,
  Package,
  Warehouse,
  ShoppingCart,
  FileText,
  TrendingUp,
  Syringe,
  Settings,
  FileBarChart,
  User,
  FileSearch,
  BedDouble,
  BedSingle,
  Receipt,
  Wallet,
} from "lucide-react";
import { createElement } from "react";
import { CustomIcons } from "@/components/icons/CustomIcons";
import type { NavigationConfig } from "@/lib/types/navigation";

/**
 * Centralized navigation configuration
 *
 * This configuration maps all sidebar navigation items to their corresponding
 * features in the database. The navigation system automatically filters items
 * based on:
 * 1. Active modules (from customer_modules table)
 * 2. Feature permissions (from get_user_all_permissions RPC)
 * 3. Custom visibility rules
 *
 * Adding a new navigation item:
 * 1. Add the feature to the database (migration)
 * 2. Add the item to the appropriate section below
 * 3. Set featureKey to match features.value from database
 * 4. Navigation will automatically filter based on permissions
 */
export const NAVIGATION_CONFIG: NavigationConfig = [
  // ========================================
  // SECTION: Principal (Core Features)
  // ========================================
  {
    title: "sectionPrincipal",
    moduleKey: "core",
    items: [
      {
        title: "patients",
        url: "/patients",
        icon: Users,
        description: "patientsDescription",
        featureKey: "patients",
        requiredAction: "view",
        alwaysVisible: true,
      },
      {
        title: "calendar",
        url: "/calendar",
        icon: Calendar,
        description: "calendarDescription",
        featureKey: "appointments",
        requiredAction: "view",
        alwaysVisible: true,
      },
    ],
  },

  // ========================================
  // SECTION: Flowboard (service flow — its own paid module)
  // ========================================
  {
    title: "sectionFlow",
    moduleKey: "flowboard",
    items: [
      {
        title: "flow",
        url: "/calendar/flowboard",
        icon: FileSearch,
        description: "flowDescription",
        featureKey: "flowboard",
        requiredAction: "view",
      },
    ],
  },

  // ========================================
  // SECTION: Catalog (basic product/service registration — core surface)
  // ========================================
  {
    title: "sectionCatalog",
    moduleKey: "core",
    items: [
      {
        title: "catalog",
        url: "/catalog",
        icon: Package,
        description: "catalogDescription",
        featureKey: "catalog",
        requiredAction: "view",
      },
    ],
  },

  // ========================================
  // SECTION: Stock
  // ========================================
  {
    title: "sectionStock",
    moduleKey: "stock",
    items: [
      {
        title: "stock",
        url: "/products-services",
        icon: Warehouse,
        description: "stockDescription",
        featureKey: "stock",
        requiredAction: "view",
      },
    ],
  },

  // ========================================
  // SECTION: Financial (sales / POS)
  // ========================================
  {
    title: "sectionFinancial",
    moduleKey: "financial",
    items: [
      {
        title: "sales",
        url: "/sales",
        icon: ShoppingCart,
        description: "salesDescription",
        featureKey: "sales",
        requiredAction: "view",
      },
    ],
  },

  // ========================================
  // SECTION: Financial ledger (cashbox / receivables / payables / DRE)
  // ========================================
  {
    title: "sectionFinancialLedger",
    moduleKey: "financial_ledger",
    items: [
      {
        title: "financial",
        url: "/financial",
        icon: Wallet,
        description: "financialDescription",
        alternativeFeatures: [
          { featureKey: "financial_receivables", requiredAction: "view" },
          { featureKey: "financial_payables", requiredAction: "view" },
          { featureKey: "financial_cashbox", requiredAction: "view" },
          { featureKey: "financial_ledger", requiredAction: "view" },
          { featureKey: "financial_overview", requiredAction: "view" },
        ],
      },
    ],
  },

  // ========================================
  // SECTION: Reports (visibility follows the 'reports'/'applications'/'vaccines'
  // feature permission, all core; individual report tabs are further gated by
  // their own module elsewhere)
  // ========================================
  {
    title: "sectionReports",
    items: [
      {
        title: "reports",
        url: "/reports",
        icon: TrendingUp,
        description: "reportsDescription",
        featureKey: "reports",
        requiredAction: "view",
        alternativeFeatures: [
          { featureKey: "reports", requiredAction: "view" },
          { featureKey: "applications", requiredAction: "view" },
          { featureKey: "vaccines", requiredAction: "view" },
        ],
      },
    ],
  },


  // ========================================
  // SECTION: Banho e Tosa (Grooming)
  // ========================================
  {
    title: "sectionGrooming",
    moduleKey: "grooming",
    items: [
      {
        title: "grooming",
        url: "/grooming",
        icon: () => createElement(CustomIcons, { name: "hair-dryer", className: "h-4 w-4" }),
        description: "groomingDescription",
        featureKey: "grooming",
        requiredAction: "view",
      },
    ],
  },

  // ========================================
  // SECTION: Internação
  // ========================================
  {
    title: "sectionHospitalization",
    moduleKey: "hospitalization",
    items: [
      {
        title: "hospitalization",
        url: "/hospitalization",
        icon: BedSingle,
        description: "hospitalizationDescription",
        featureKey: "hospitalization",
        requiredAction: "view",
      },
    ],
  },

  {
    title: "sectionDaycareHotel",
    moduleKeysAny: ["day_care", "hotel"],
    items: [
      {
        title: "daycareHotel",
        url: "/daycare-hotel",
        icon: BedDouble,
        description: "daycareHotelDescription",
        alternativeFeatures: [
          { featureKey: "daycare", requiredAction: "view" },
          { featureKey: "hotel", requiredAction: "view" },
        ],
      },
    ],
  },

  // ========================================
  // SECTION: Fiscal
  // ========================================
  {
    title: "sectionFiscal",
    moduleKey: "fiscal",
    items: [
      {
        title: "taxes",
        url: "/taxes/settings",
        icon: Receipt,
        description: "taxesDescription",
        alwaysVisible: true,
      },
    ],
  },

  // ========================================
  // SECTION: Administração (Administration)
  // No module restriction - visible based on permissions only
  // ========================================
  {
    title: "sectionAdmin",
    items: [
      {
        title: "settings",
        url: "/settings",
        icon: Settings,
        description: "settingsDescription",
        // Special case: show if user has admin_settings permission OR has access to multiple clinics
        // Owners should always see Settings since they have all permissions
        // We check for owner-specific permissions (admin_users, user_permission) as owners have these
        customVisibilityCheck: (permissions, clinicsCount) => {
          // Check if user has admin_settings permission
          if (permissions.admin_settings?.view) {
            return true;
          }

          // Check if user has access to multiple clinics
          if (clinicsCount > 1) {
            return true;
          }

          // Check if user is likely an owner by checking for owner-specific permissions
          // Owners have permissions like admin_users, user_permission, etc.
          // If user has admin_users permission, they're likely an owner
          if (permissions.admin_users?.view || permissions.user_permission?.view) {
            return true;
          }

          return false;
        },
      },
    ],
  },
];
